/**
 * @file   src\Pages\AdminUsers.tsx
 * @brief  Admin Users page.
 * @date   JUL, 2024
 * @author ZCO Engineer
 * @copyright (c) 2024, ZCO
 */

import {
  React,
  useState,
  Container,
  Table,
  Button,
  useNavigate,
  useOutletContext,
  useEffect,
  Modal,
} from '../utils/thirdpartyComponents';
import "../styles/subscriptions.scss";
import CWSelect from '../components/CWSelect';
import EditIcon from '../assets/images/icons/editicon';
import DeleteIcon from '../assets/images/icons/deleteicon';
import AddIcon from '../assets/images/icons/addicon';
import { pageURLs } from '../utils/pageUrls';
import { AppContextType } from '../interfaces/generalInterface';
import axios from '../config/axiosConfig';
import { SubAdminApi } from '../utils/apiUrls';
import SmallLoader from '../components/SmallLoader';
import { MessageToaster } from '../utils/toastUtil';

interface UserRecord {
  FullName: string;
  Email: string;
  PermissionType: string;
  UserGUID: string;
  UserID: number
}

const AdminUsers = () => {
  const navigate = useNavigate();
  const toaster = new MessageToaster

  // Access the context
  const { updateData } = useOutletContext<AppContextType>();

  // To display heading
  useEffect(() => {
    handleUpdate();
    listUsers();
  }, []);

  // Update the data
  const handleUpdate = () => {
    updateData({
      heading: "Admin Users",
      url: `/superadmin/${pageURLs.dashboard}`,
      showBackButton: false,
      showMainMenu: true,
      activeLink: "admin-users",
    });
  };

  const options = [
    { label: 'Full Permission', value: 1 },
    { label: 'Car Wash Management', value: 2 },
  ]

  const [loading, setLoading] = useState(false);
  const [showDeleteConfirmation, setShowDeleteConfirmation] = useState<boolean>(false);
  const [showPermissionConfirmation, setShowPermissionConfirmation] = useState<boolean>(false);
  const [selectedAdminId, setSelectedAdminId] = useState<string>("");
  const [userGuid, setUserGuid] = useState<string>("");
  const [permissionType, setPermissionType] = useState<number>(0);
  const [userRecords, setUserRecords] = useState<UserRecord[]>([]);

  const listUsers = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(SubAdminApi.LIST_ADMIN_USER);
      setLoading(false);

      if (data?.error === 0) {
        setUserRecords(data.data.list || []);
      } else {
        setUserRecords([]);
      }
    } catch (error:any) {
      setLoading(false);
      console.error('Error fetching user records:', error.message);
      setUserRecords([]);
    }
  };

  const confirmDelete = (id: string) => {
    setSelectedAdminId(id)
    setShowDeleteConfirmation(true)
  }



  // Delete Admin
  const deleteAdmin = async () => {
    try {
      setLoading(true)
      const { data } = await axios.post(SubAdminApi.DELETE_ADMIN_USER, { "userGuid": selectedAdminId });
      setLoading(false)

      if (data?.error === 0) {
        navigate(`/superadmin/${pageURLs.admin_users}`)
        setShowDeleteConfirmation(false)
        // Reload page
        listUsers()
      }
      else {
        // show error message
        toaster.toastError(data?.message);
      }
    } catch (error: any) {
      throw new Error(error?.response?.data?.message || error?.message);
    }
  }

  //Hide ppopup
  const handleOnHide = () => {
    setShowDeleteConfirmation(false)
  }

  const handlePermissionOnHide = () => {
    setShowPermissionConfirmation(false)
  }

  const permissionUpdate = (UserGUID: string, permission: any) => {
    setPermissionType(permission[0].value)
    setUserGuid(UserGUID)
    setShowPermissionConfirmation(true)
  }

  const changeAdminPermission = async () => {

    const AdminUpdate = await axios.post(SubAdminApi.EDIT_ADMIN_USER, {

      permissionType: permissionType,
      userGuid: userGuid
    });

    setLoading(false)
    setShowPermissionConfirmation(false)
    if (AdminUpdate?.data?.error === 0) {
      toaster.toastSuccess(AdminUpdate?.data?.message);
    }
    else {
      toaster.toastError(AdminUpdate?.data?.message);
    }
  }

  return (
    <div className="manage-carwash-container manage-admin-subscription">
      <Container>
        <div className="manage-header-block d-flex justify-content-end">
          <Button
            title="Add New User"
            type="submit"
            variant="primary"
            className="primary-btn ms-auto d-flex align-items-center"
            onClick={() => navigate(`/superadmin/${pageURLs.addnew_user}`)}
          >
            Add New User
            <AddIcon />
          </Button>
        </div>
        <div className="cw-table-wrap tbl-visible">
          <Table responsive className="m-0">
            <thead>
              <tr>
                <th>Name</th>
                <th>Email Address</th>
                <th>Permission Type</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {userRecords.map((user, index) => (
                <tr key={index}>
                  <td>{user.FullName}</td>
                  <td>{user.Email}</td>
                  <td>
                    <CWSelect
                      id="permissiontype"
                      name="permissiontype"
                      options={options}
                      className="hide-input"
                      dropdownGap={0}
                      dropdownPosition="auto"
                      multi={false}
                      values={options.filter(
                        (option) => option.label == user.PermissionType
                      )}
                      onChange={(e) => permissionUpdate(user.UserGUID, e)}
                    />
                  </td>
                  <td className="nowrap" width="10%">
                    <div className="active-group d-flex align-items-center">
                      <Button
                        title="Edit"
                        variant="light"
                        className="light-btn action-btn"
                        onClick={() =>
                          navigate(`/superadmin/${pageURLs.edit_user}/${user.UserID}`)
                        }
                      >
                        <EditIcon />
                      </Button>
                      <Button
                        title="Delete"
                        variant="light"
                        className="light-btn action-btn"
                        onClick={() => confirmDelete(user.UserGUID)}
                      >
                        <DeleteIcon />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </div>
        <Modal
          show={showPermissionConfirmation}
          onHide={handlePermissionOnHide}
          centered
          className='add-modal'
        >
          <Modal.Body className='text-center'>
            <h5 className='f-bold text-center'>Are you sure you want to change the permission?</h5>
            <div className="btn-sec d-grid">
              <Button title='Cancel' variant="light" className="secondary-btn" onClick={handlePermissionOnHide}>Cancel</Button>
              <Button title='Yes' variant="primary" className='primary-btn btn-main m-0' onClick={changeAdminPermission}>Yes</Button>
            </div>
          </Modal.Body>
          {loading && <SmallLoader />}
        </Modal>
        <Modal
          show={showDeleteConfirmation}
          onHide={handleOnHide}
          centered
          className='add-modal'
        >
          <Modal.Body className='text-center'>
            <h5 className='f-bold text-center'>Are you sure you want to delete this admin?</h5>
            <div className="btn-sec d-grid">
              <Button title='Cancel' variant="light" className="secondary-btn" onClick={handleOnHide}>Cancel</Button>
              <Button title='Yes' variant="primary" className='primary-btn btn-main m-0' onClick={deleteAdmin}>Yes</Button>
            </div>
          </Modal.Body>
          {loading && <SmallLoader />}
        </Modal>
      </Container>
      {loading && <SmallLoader />}
    </div>

  );
};

export default AdminUsers;
